import { c as _c } from "react/compiler-runtime";
import { IcoClose16, IcoDotsHorizontal16 } from '@onefootprint/icons';
import type { LitePlaybook, OnboardingStatus, PublicWorkflowKind } from '@onefootprint/request-types/dashboard';
import { Badge, Button, CaptureEscKeydown, Dropdown, IconButton, type OnEscKeydownHandler } from '@onefootprint/ui';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@/components';
import SandboxToggle from '@/components/sandbox-toggle';
import { useGetPlaybookVersionDisplayName } from '@/hooks/use-get-playbook-version-display-name';
import { useOpenPage } from '@/hooks/use-open-page';
import useSession from '@/hooks/use-session';
import DeactivateWorkflowItem from './deactivate-workflow-item';
import ErrorWorkflowItem from './error-workflow-item';
import ResumeWorkflowItem from './resume-workflow-item';
type HeaderProps = {
  playbook?: LitePlaybook;
  kind: PublicWorkflowKind;
  status: OnboardingStatus;
  deactivated: boolean;
  artifactsCount: number;
  onViewArtifacts: () => void;
  fpId: string;
  entityKind: 'user' | 'business';
  onboardingId: string;
};
export const Header = t0 => {
  const $ = _c(59);
  const {
    playbook,
    kind,
    status,
    deactivated,
    artifactsCount,
    onViewArtifacts,
    fpId,
    entityKind,
    onboardingId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardingTraceDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const handleOpenPage = useOpenPage();
  const router = useRouter();
  const {
    getPlaybookVersionDisplayName
  } = useGetPlaybookVersionDisplayName();
  const playbookName = getPlaybookVersionDisplayName(playbook, kind);
  const {
    data: session
  } = useSession();
  let t2;
  if ($[1] !== handleOpenPage || $[2] !== playbook) {
    t2 = event => {
      if (!playbook) {
        return;
      }
      const {
        playbookId,
        id
      } = playbook;
      handleOpenPage(event, `/playbooks/${playbookId}`, {
        version: id
      });
    };
    $[1] = handleOpenPage;
    $[2] = playbook;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleViewPlaybook = t2;
  const entityUrlPath = entityKind === "business" ? "businesses" : "users";
  let t3;
  if ($[4] !== entityUrlPath || $[5] !== fpId || $[6] !== router) {
    t3 = () => {
      if (window.history.length > 1) {
        router.back();
        return;
      }
      router.push(`/${entityUrlPath}/${fpId}`);
    };
    $[4] = entityUrlPath;
    $[5] = fpId;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== router) {
    t4 = [router];
    $[8] = router;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = {
      handler: t3,
      deps: t4
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onNavigateBack = t5;
  const t6 = "min-h-11 flex flex-wrap z-10 shrink-0 justify-between items-center gap-2 py-2 px-4 w-full border-b border-tertiary bg-primary md:h-11 md:flex-nowrap md:gap-0 md:py-0";
  let t7;
  if ($[13] !== onNavigateBack) {
    t7 = <CaptureEscKeydown id="navigate-back" onEscKeydown={onNavigateBack} />;
    $[13] = onNavigateBack;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = "flex gap-2 items-center min-w-0 md:gap-4 md:min-w-auto";
  let t9;
  if ($[15] !== t) {
    t9 = t(_temp);
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== onNavigateBack.handler || $[18] !== t9) {
    t10 = <div className="items-center"><IconButton icon={IcoClose16} aria-label={t9} onClick={onNavigateBack.handler} size="compact" /></div>;
    $[17] = onNavigateBack.handler;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = "flex flex-col gap-1";
  const t12 = "flex flex-row flex-wrap gap-x-4 gap-y-1 items-center min-h-[24px] md:flex-nowrap md:gap-4";
  const t13 = "text-label-2 text-primary";
  const t14 = t(_temp2, {
    playbookName
  });
  let t15;
  if ($[20] !== t14) {
    t15 = <h2 className={t13}>{t14}</h2>;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== status) {
    t16 = <StatusBadge status={status} />;
    $[22] = status;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== deactivated || $[25] !== t) {
    t17 = deactivated && <Badge variant="neutral">{t(_temp3)}</Badge>;
    $[24] = deactivated;
    $[25] = t;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16 || $[28] !== t17) {
    t18 = <div className="flex items-center gap-2">{t16}{t17}</div>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t15 || $[31] !== t18) {
    t19 = <div className={t11}><div className={t12}>{t15}{t18}</div></div>;
    $[30] = t15;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t10 || $[34] !== t19) {
    t20 = <div className={t8}>{t10}{t19}</div>;
    $[33] = t10;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = artifactsCount === 0;
  let t22;
  if ($[36] !== t) {
    t22 = t(_temp4);
    $[36] = t;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = `${t22} (${artifactsCount})`;
  let t24;
  if ($[38] !== onViewArtifacts || $[39] !== t21 || $[40] !== t23) {
    t24 = <Button variant="secondary" onClick={onViewArtifacts} size="compact" disabled={t21}>{t23}</Button>;
    $[38] = onViewArtifacts;
    $[39] = t21;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== handleViewPlaybook || $[43] !== playbook || $[44] !== t) {
    t25 = playbook && <Button variant="secondary" onClick={handleViewPlaybook} size="compact">{t(_temp5)}</Button>;
    $[42] = handleViewPlaybook;
    $[43] = playbook;
    $[44] = t;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SandboxToggle />;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== fpId || $[48] !== onboardingId || $[49] !== session.user?.isRiskOpsEmployee) {
    t27 = session.user?.isRiskOpsEmployee && <Dropdown.Root><Dropdown.Trigger asChild={true}><IconButton aria-label="More actions" icon={IcoDotsHorizontal16} size="compact" variant="outline" /></Dropdown.Trigger><Dropdown.Portal><Dropdown.Content align="end"><ResumeWorkflowItem onboardingId={onboardingId} /><ErrorWorkflowItem fpId={fpId} onboardingId={onboardingId} /><DeactivateWorkflowItem fpId={fpId} onboardingId={onboardingId} /></Dropdown.Content></Dropdown.Portal></Dropdown.Root>;
    $[47] = fpId;
    $[48] = onboardingId;
    $[49] = session.user?.isRiskOpsEmployee;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t24 || $[52] !== t25 || $[53] !== t27) {
    t28 = <div className="flex flex-wrap gap-2 w-full justify-start items-center md:flex-nowrap md:w-auto md:justify-end">{t24}{t25}{t26}{t27}</div>;
    $[51] = t24;
    $[52] = t25;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t20 || $[56] !== t28 || $[57] !== t7) {
    t29 = <header className={t6}>{t7}{t20}{t28}</header>;
    $[55] = t20;
    $[56] = t28;
    $[57] = t7;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  return t29;
};
function _temp($) {
  return $.goBack;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.deactivated;
}
function _temp4($_2) {
  return $_2.artifacts.viewButton;
}
function _temp5($_3) {
  return $_3.viewPlaybook;
}